/**
 * Pure helpers shared by the chapter actions (`actions.ts`) and the exercise actions
 * (`exercise-actions.ts`). No state, no store: each takes the value it works on.
 */

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const cloneJson = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

/** `undefined` leaves the key alone, `null` deletes it, anything else assigns. */
export const patchOptional = <T extends object, K extends keyof T>(
  target: T,
  key: K,
  value: T[K] | null | undefined,
): void => {
  if (value === undefined) {
    return
  }
  if (value === null) {
    Reflect.deleteProperty(target, key)
    return
  }
  target[key] = value
}

export const patchRequired = <T extends object, K extends keyof T>(
  target: T,
  key: K,
  value: T[K] | undefined,
): void => {
  if (value === undefined) {
    return
  }
  target[key] = value
}

/** Moves `list[index]` by `delta`; out-of-range source or destination is a no-op. */
export const moveWithin = <T>(list: T[], index: number, delta: number): void => {
  const target = index + delta
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) {
    return
  }
  const [item] = list.splice(index, 1)
  if (item === undefined) {
    return
  }
  list.splice(target, 0, item)
}

/** After removing `removedIndex`: the item that shifted into the slot, else the previous one. */
export const neighbourId = (list: { id: string }[], removedIndex: number): string | null =>
  list[removedIndex]?.id ?? list[removedIndex - 1]?.id ?? null
